import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const SpeakingInquiryForm = () => {
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    eventType: "",
    eventDate: "",
    audienceSize: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Delivered through the same contact-submit edge function as /contact;
    // the event fields are folded into the message body.
    const eventDetails = [
      formData.organization && `Organization: ${formData.organization}`,
      formData.eventType && `Event type: ${formData.eventType}`,
      formData.eventDate && `Event date: ${formData.eventDate}`,
      formData.audienceSize && `Audience size: ${formData.audienceSize}`,
    ].filter((line): line is string => Boolean(line));
    const details = ["[Speaking Inquiry]", ...eventDetails, "", formData.message].join("\n");

    try {
      const { data, error } = await supabase.functions.invoke("contact-submit", {
        body: { name: formData.name, email: formData.email, message: details },
      });
      if (error || data?.error) throw new Error(data?.error ?? error?.message);
      setIsSubmitted(true);
      toast({
        title: "Inquiry Submitted!",
        description: "We'll be in touch within 48 hours.",
      });
    } catch {
      toast({
        title: "Something went wrong",
        description:
          "Your inquiry could not be sent. Please email info@marquettabryant.com directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold mb-2">Thank You!</h3>
        <p className="text-muted-foreground">
          Your speaking inquiry has been received. We'll be in touch within 48 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="name">Your Name *</Label>
          <Input
            id="name"
            required
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Jane Smith"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder="jane@company.com"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="organization">Organization / Event Name *</Label>
        <Input
          id="organization"
          required
          value={formData.organization}
          onChange={(e) => handleChange("organization", e.target.value)}
          placeholder="ABC Corporation Annual Conference"
        />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="space-y-2">
          <Label htmlFor="eventType">Event Type *</Label>
          <Select 
            value={formData.eventType} 
            onValueChange={(value) => handleChange("eventType", value)}
            required
          >
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="keynote">Keynote</SelectItem>
              <SelectItem value="panel">Panel Discussion</SelectItem>
              <SelectItem value="workshop">Workshop</SelectItem>
              <SelectItem value="webinar">Virtual/Webinar</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="eventDate">Event Date *</Label>
          <Input
            id="eventDate"
            type="date"
            required
            value={formData.eventDate}
            onChange={(e) => handleChange("eventDate", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="audienceSize">Audience Size</Label>
          <Select 
            value={formData.audienceSize} 
            onValueChange={(value) => handleChange("audienceSize", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="under-50">Under 50</SelectItem>
              <SelectItem value="50-100">50-100</SelectItem>
              <SelectItem value="100-500">100-500</SelectItem>
              <SelectItem value="500-1000">500-1,000</SelectItem>
              <SelectItem value="1000+">1,000+</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Tell Us About Your Event *</Label>
        <Textarea
          id="message"
          required
          rows={4}
          value={formData.message}
          onChange={(e) => handleChange("message", e.target.value)}
          placeholder="What's the focus of your event? What topics are you interested in? Any specific goals for the session?"
        />
      </div>

      <Button type="submit" size="lg" variant="premium" className="w-full group" disabled={isSubmitting}>
        {isSubmitting ? "Sending..." : "Submit Inquiry"}
        <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
      </Button>
    </form>
  );
};

export default SpeakingInquiryForm;
